import React from "react";
import { ChevronDown, Layers } from "lucide-react";
import { SeriesDTO } from "@/generated-client";
import { Localizer } from "@/main/react/Types";
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClientFactory } from "@/main/common/ClientFactory";
import { filterBookmarksSeries } from "@/main/react/series/BookmarksSeriesLocalization";
import { cn } from "@/lib/utils";
import logger from "@/lib/logger";
import { getOptimizedSeavienImageUrl, SMALL_THUMBNAIL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";

/**
 * The series twin of `DiscussionGroupSummary`, and deliberately its structural copy: same avatar
 * group, same "<lead-in> <primary link> and N others" line, same overflow popover.
 *
 * <p>Three things differ from the group summary, each recorded in the owning design doc
 * (`designs/05-needs-refresh/2026-04-12-discussion-discoverability-phase-1.md`, closure note):
 * <ol>
 *   <li><b>It fetches its own data</b>, gated on the discussion's own series count. The group
 *       summary is fed by state the page already keeps for the group FILTER; there is no series
 *       equivalent, and adding one would put a request on every discussion page load. With
 *       `seriesCount` at 0 this component is not rendered at all, so a post in no series costs
 *       nothing.</li>
 *   <li><b>Bookmarks collections are removed.</b> A member's automatic Bookmarks series is a
 *       private reading list. The server already hides other people's, but it correctly returns
 *       the viewer's OWN — so without this filter an author re-reading a post they had saved would
 *       see "Part of Bookmarks" printed above their own title.</li>
 *   <li><b>It renders no skeleton and no error state.</b> Rule 32: this block sits inside the
 *       discussion island above the title, and a placeholder there on every page load — for a
 *       block most posts do not have — is a regression in its own right. A failed fetch renders
 *       null, which is exactly what the page held before it mounted.</li>
 * </ol>
 */
type DiscussionSeriesSummaryLocalizationKey =
    | "discussion.section.series"
    | "discussion.series.summary.in"
    | "discussion.groups.summary.and_others";

function localize(localizer: Localizer, key: DiscussionSeriesSummaryLocalizationKey, ...args: string[]): string {
    return localizer(key, ...args);
}

/**
 * Discovery-friendly first, per the design's ordering strategy: a public series with an audience
 * is the one worth putting a reader's one glance on.
 *
 * <p>The bookmarks rank is vacuous today because the caller filters them out before sorting. It is
 * kept because it is the design's stated first key and because the filter is one refactor away
 * from moving; a comparator that silently depended on its input being pre-filtered would be the
 * kind of coupling nothing tests.
 */
export function compareDiscussionSeries(left: SeriesDTO, right: SeriesDTO): number {
    const bookmarksRank = Number(Boolean(left.bookmarksCollection)) - Number(Boolean(right.bookmarksCollection));
    if (bookmarksRank !== 0) {
        return bookmarksRank;
    }

    const privateRank = Number(Boolean(left.privateAccess)) - Number(Boolean(right.privateAccess));
    if (privateRank !== 0) {
        return privateRank;
    }

    const followersDiff = (right.followersCount ?? 0) - (left.followersCount ?? 0);
    if (followersDiff !== 0) {
        return followersDiff;
    }

    const discussionsDiff = (right.discussionsCount ?? 0) - (left.discussionsCount ?? 0);
    if (discussionsDiff !== 0) {
        return discussionsDiff;
    }

    return (right.publicId ?? "").localeCompare(left.publicId ?? "");
}

/** Distinct by public id, bookmarks removed, then ordered. Pure, so it is tested directly. */
export function orderDiscussionSeries(fetchedSeries: readonly SeriesDTO[] | null | undefined): SeriesDTO[] {
    const distinct = new Map<string, SeriesDTO>();
    filterBookmarksSeries(fetchedSeries ?? []).forEach((series) => {
        const publicId = series?.publicId?.trim();
        if (!publicId || distinct.has(publicId)) {
            return;
        }
        distinct.set(publicId, series);
    });
    return Array.from(distinct.values()).sort(compareDiscussionSeries);
}

function getAvatarFallback(name?: string | null): string {
    const trimmedName = name?.trim();
    if (!trimmedName) {
        return "S";
    }
    return trimmedName.charAt(0).toUpperCase();
}

function getSeriesHref(series: SeriesDTO): string | undefined {
    return series.slug ? `/series/${encodeURIComponent(series.slug)}` : undefined;
}

function seriesLabel(series: SeriesDTO, localizer: Localizer): string {
    const title = series.title?.trim();
    return title ? title : localize(localizer, "discussion.section.series");
}

// One row in the overflow dropdown: the series' image + title (+ a language-neutral post count),
// linking to the series. Renders as a plain <div> row when the series has no slug to link to.
export function SeriesOverflowRow({ series, localizer }: { series: SeriesDTO; localizer: Localizer }) {
    const href = getSeriesHref(series);
    const avatarImageUrl = getOptimizedSeavienImageUrl(series.thumbnailUrl, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
    const name = seriesLabel(series, localizer);
    const discussionsCount = series.discussionsCount ?? 0;

    const rowClass = "flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-accent";
    const content = (
        <>
            <Avatar size="sm" className="shrink-0 bg-muted">
                <AvatarImage src={avatarImageUrl} alt={series.title ?? undefined} />
                <AvatarFallback>{getAvatarFallback(series.title)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text-main">{name}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Layers className="size-3.5 shrink-0" />
                    <span>{discussionsCount}</span>
                </div>
            </div>
        </>
    );

    return href ? (
        <a href={href} className={rowClass} aria-label={name}>
            {content}
        </a>
    ) : (
        <div className={rowClass}>{content}</div>
    );
}

function SeriesOverflowPopover({
    series,
    otherSeriesCount,
    localizer,
}: {
    series: SeriesDTO[];
    otherSeriesCount: number;
    localizer: Localizer;
}) {
    return (
        <Popover>
            <PopoverTrigger
                type="button"
                aria-label={localize(localizer, "discussion.section.series")}
                data-playwright-id="discussion-series-overflow-trigger"
                /*
                 * The group summary's trigger recipe, unchanged and for its reasons (`DISCUSSION-31`):
                 * `[@media(pointer:coarse)]:min-h-11` rather than a centred `touch-hit-44` slug, which
                 * at this pitch would overlap the link above and steal its taps; and the app-wide
                 * focus-ring triple rather than a per-component ring. Divergence between the two
                 * summaries here would be a bug in one of them.
                 */
                className="inline-flex items-center gap-0.5 rounded-sm font-medium text-text-main underline decoration-dotted underline-offset-2 touch-manipulation hover:decoration-solid [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
                <span>{localize(localizer, "discussion.groups.summary.and_others", String(otherSeriesCount))}</span>
                <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-72 p-1"
                data-playwright-id="discussion-series-overflow-menu"
            >
                <div className="max-h-72 overflow-y-auto">
                    {series.map((item) => (
                        <SeriesOverflowRow key={item.publicId} series={item} localizer={localizer} />
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

/** The presentational half, exported so a test can drive it without a fetch. */
export function DiscussionSeriesSummaryView({
    series,
    localizer,
    className,
    maxVisibleAvatars = 3,
}: {
    series: SeriesDTO[];
    localizer: Localizer;
    className?: string;
    maxVisibleAvatars?: number;
}) {
    if (!series.length) {
        return null;
    }

    const primarySeries = series[0];
    const otherSeriesCount = Math.max(0, series.length - 1);
    const visibleSeries = series.slice(0, maxVisibleAvatars);
    const hiddenSeriesCount = Math.max(0, series.length - visibleSeries.length);
    const primaryHref = getSeriesHref(primarySeries);

    return (
        <div
            className={cn(
                "flex min-w-0 flex-wrap items-center gap-3 text-sm text-muted-foreground",
                className,
            )}
            data-playwright-id="discussion-series-summary"
        >
            {/* `DISCUSSION-80` gave this stack a `min-w-[4.5rem]` floor so it shared a label origin with the
                group twin's row. `DISCUSSION-120` (V52 a): since U39 the page renders ONE combined
                provenance line whenever a post has both, so the two rows never stand together and the floor
                only ever padded a lone stack ~60 CSS px short of its label. Keep the two twins identical.

                `DISCUSSION-119` (V52 a): the stack is DECORATIVE. Each avatar used to be its own ~24 px
                link, overlapped by its neighbour, beside the text link to the same place, so a thumb could
                open the wrong series and the keyboard met duplicate tab stops. Navigation is the text link
                and the overflow popover; the stack is hidden from AT and holds no anchors. */}
            <AvatarGroup className="shrink-0" aria-hidden="true" data-playwright-id="discussion-series-summary-avatars">
                {visibleSeries.map((item) => {
                    const avatarImageUrl = getOptimizedSeavienImageUrl(item.thumbnailUrl, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
                    return (
                        <Avatar key={item.publicId} size="sm" className="bg-muted">
                            <AvatarImage src={avatarImageUrl} alt="" />
                            <AvatarFallback>{getAvatarFallback(item.title)}</AvatarFallback>
                        </Avatar>
                    );
                })}
                {hiddenSeriesCount > 0 ? (
                    <AvatarGroupCount>+{hiddenSeriesCount}</AvatarGroupCount>
                ) : null}
            </AvatarGroup>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <div className="min-w-0">
                    <span>{localize(localizer, "discussion.series.summary.in")} </span>
                    {/* `DISCUSSION-80`, second half — the series TITLE does not break across lines, the
                        same treatment (and the same reasoning) as the group name. See the group twin for
                        why `align-bottom` is load-bearing. */}
                    {primaryHref ? (
                        <a
                            href={primaryHref}
                            className="inline-block max-w-full truncate align-bottom font-medium text-text-main hover:underline"
                        >
                            {seriesLabel(primarySeries, localizer)}
                        </a>
                    ) : (
                        <span className="inline-block max-w-full truncate align-bottom font-medium text-text-main">
                            {seriesLabel(primarySeries, localizer)}
                        </span>
                    )}
                    {otherSeriesCount > 0 ? (
                        <>
                            {" "}
                            <SeriesOverflowPopover
                                series={series}
                                otherSeriesCount={otherSeriesCount}
                                localizer={localizer}
                            />
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/**
 * The mounted block. Renders nothing at all — not a skeleton, not an empty state — until it has a
 * series to name.
 *
 * @param seriesCount the discussion's own `addedToSeriesCount`. At 0 nothing is fetched. Callers
 *                    should skip rendering this component entirely in that case, which keeps the
 *                    DOM byte-identical to the pre-feature page; the guard here is the second line.
 */
export default function DiscussionSeriesSummary({
    discussionPublicId,
    seriesCount,
    isDeleted = false,
    localizer,
    className,
}: {
    discussionPublicId?: string;
    seriesCount?: number;
    isDeleted?: boolean;
    localizer: Localizer;
    className?: string;
}) {
    const [series, setSeries] = React.useState<SeriesDTO[]>([]);
    const shouldLoad = Boolean(discussionPublicId) && !isDeleted && Number(seriesCount ?? 0) > 0;

    React.useEffect(() => {
        let cancelled = false;
        if (!shouldLoad || !discussionPublicId) {
            setSeries([]);
            return () => {
                cancelled = true;
            };
        }

        const load = async () => {
            try {
                const api = ClientFactory.getAjaxPaginationRestControllerApi();
                const response = await api.seriesForDiscussion(discussionPublicId, undefined);
                if (cancelled) {
                    return;
                }
                setSeries(orderDiscussionSeries(response?.data ?? []));
            } catch (error) {
                if (cancelled) {
                    return;
                }
                // Deliberately silent on screen: the block simply stays absent. See rule 32 and the
                // class comment — the alternative is an error box above the title of a post that is
                // perfectly readable.
                logger.error("[DiscussionSeriesSummary] failed to load the discussion's series", error);
                setSeries([]);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [discussionPublicId, shouldLoad]);

    if (!series.length) {
        return null;
    }

    return <DiscussionSeriesSummaryView series={series} localizer={localizer} className={className} />;
}
